/** Iklan HQ — seller-funded coupons a buyer claims from an HQ surface. */
import { PLACEHOLDER_EMAIL_DOMAIN } from "./checkoutEmail";

export const AD_COUPON_TTL_HOURS = 72;
export const AD_CODE_PREFIX = "IKL";
const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const MYT_OFFSET_MS = 8 * 3600_000;
const DAY_MS = 24 * 3600_000;

export type AdCouponType = "fixed" | "percent";

export interface ClaimedAdCoupon {
  code: string;
  type: AdCouponType;
  value: number;
  minSubtotal: number;
  expiresAt: string;
  storeId: string;
  productId: string;
  /** True when this email already held a live code for the offer. */
  reused: boolean;
}

export interface AdCouponTemplate {
  type: AdCouponType;
  value: number;
  minSubtotal: number;
  dailyBudget: number | null;
}

/** What one claim is expected to cost the seller, in cents, for budget pacing. */
export function committedCents(type: AdCouponType, value: number, priceCents: number): number {
  if (type === "fixed") return Math.min(Math.round(value * 100), priceCents);
  return Math.round((priceCents * value) / 100);
}

/** Start of the Malaysian calendar day that contains `now`, as an ISO string. */
export function mytDayStartIso(now: Date): string {
  const t = now.getTime();
  return new Date(Math.floor((t + MYT_OFFSET_MS) / DAY_MS) * DAY_MS - MYT_OFFSET_MS).toISOString();
}

/** A null budget means unlimited; otherwise the new claim must still fit. */
export function budgetAllows(spentCents: number, addCents: number, budget: number | null): boolean {
  if (budget == null) return true;
  return spentCents + addCents <= Math.round(budget * 100);
}

export function claimExpiry(now: Date): string {
  return new Date(now.getTime() + AD_COUPON_TTL_HOURS * 3600_000).toISOString();
}

/** What an Iklan HQ claim mints: an `IKL…` code good for one use. Only these are looked up in D1. */
export function looksLikeAdCode(code: string, maxUses: number | null): boolean {
  return maxUses === 1 && code.length === AD_CODE_PREFIX.length + 7 && code.startsWith(AD_CODE_PREFIX);
}

/** `IKL` + 7 unambiguous characters, from caller-supplied random bytes. */
export function mintAdCode(bytes: Uint8Array): string {
  let s = AD_CODE_PREFIX;
  for (let i = 0; i < 7; i++) s += CODE_ALPHABET[bytes[i % bytes.length] % CODE_ALPHABET.length];
  return s;
}

export function normEmail(e: string | null | undefined): string {
  return (e ?? "").trim().toLowerCase();
}

/** A bound code only redeems for the email that claimed it. */
export function boundEmailOk(boundEmail: string | null | undefined, buyerEmail: string | null | undefined): boolean {
  if (!boundEmail) return true;
  const b = normEmail(buyerEmail);
  return b !== "" && b === normEmail(boundEmail);
}

// ---- 127B: a click ticket carries the coupon to checkout ----------------------

/** The query parameter an ad's link carries its signed click ticket in. */
export const AD_TICKET_PARAM = "ikt";
/** A ticket lives as long as a claimed code does. */
export const AD_TICKET_TTL_HOURS = AD_COUPON_TTL_HOURS;
/** Attempts one ticket may mint (a mistyped email, a second try); only one of them can be paid. */
export const AD_TICKET_MAX_CLAIMS = 3;

export type AdClaimSource = "tuntut" | "auto";

/** Why a ticket brings no discount; each has its own words at checkout. */
export type AdTicketMiss =
  | "expired"
  | "wrong_store"
  | "offer_not_found"
  | "offer_paused"
  | "no_coupon"
  | "budget_exhausted"
  | "already_used"
  | "ticket_spent";

/** What the checkout shows for an ad's coupon: taken off, short of its minimum, or not available. */
export interface AdCouponPreview {
  offerId: string;
  state: "ok" | "gap" | "none";
  type: AdCouponType;
  value: number;
  minSubtotal: number;
  /** ok: taken off this cart; gap: what it will take off once the minimum is reached. */
  discount: number;
  /** gap: how much more the cart must hold. */
  short: number;
  subtotal: number;
  total: number;
  currency: string;
  /** none: why. */
  reason?: AdTicketMiss;
  /** When the ticket (and so this price) runs out. */
  expiresAt: string;
}

/** What the buyer is told when an ad's coupon cannot come off. */
export const AD_MISS_TEXT: Record<AdTicketMiss, string> = {
  expired: "Kupon iklan ini sudah tamat.",
  wrong_store: "Kupon iklan ini untuk kedai lain.",
  offer_not_found: "Kupon iklan ini tidak lagi tersedia.",
  offer_paused: "Kupon iklan ini tidak lagi tersedia.",
  no_coupon: "Kupon iklan ini tidak lagi tersedia.",
  budget_exhausted: "Kupon iklan hari ini sudah habis.",
  already_used: "Kupon iklan ini sudah digunakan untuk emel atau telefon ini.",
  ticket_spent: "Kupon dari pautan iklan ini sudah digunakan.",
};

/**
 * Who an ad coupon is bound to: the buyer's email, or, on a checkout where the
 * email is optional and left blank, the placeholder address the order itself
 * will carry (the phone's digits), so the same buyer is one key either way.
 */
export function adBuyerKey(email: string | null | undefined, phone: string | null | undefined): string | null {
  const e = normEmail(email);
  if (e) return e;
  const digits = (phone ?? "").replace(/\D/g, "");
  return digits.length >= 7 ? `${digits}@${PLACEHOLDER_EMAIL_DOMAIN}` : null;
}

/** The discount a coupon takes off `subtotalCents`, never more than it. */
export function couponCents(type: AdCouponType, value: number, subtotalCents: number): number {
  const off = type === "percent" ? Math.round((subtotalCents * value) / 100) : Math.round(value * 100);
  return Math.max(0, Math.min(off, subtotalCents));
}

/**
 * The checkout's verdict for a live offer's coupon against this cart: off now,
 * or `short` cents away from its minimum spend.
 */
export function adCouponVerdict(
  c: { type: AdCouponType; value: number; minSubtotal: number },
  subtotalCents: number,
): { state: "ok" | "gap"; discountCents: number; shortCents: number } {
  const minCents = Math.round(c.minSubtotal * 100);
  if (subtotalCents < minCents) {
    return { state: "gap", discountCents: couponCents(c.type, c.value, Math.max(minCents, subtotalCents)), shortCents: minCents - subtotalCents };
  }
  return { state: "ok", discountCents: couponCents(c.type, c.value, subtotalCents), shortCents: 0 };
}

/**
 * When a click ticket runs out, read from its payload without checking the
 * signature: only for showing a countdown; the server verifies before use.
 */
export function ticketExpiresAt(ticket: string | null | undefined): number | null {
  const body = (ticket ?? "").split(".")[0];
  if (!body) return null;
  try {
    const json = atob(body.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === "number" && Number.isFinite(exp) ? exp : null;
  } catch {
    return null;
  }
}

/** "71:59:58": hours can pass 24, as a claimed coupon's 72-hour window does. */
export function countdownText(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return [h, m, s % 60].map((v) => String(v).padStart(2, "0")).join(":");
}

// ---- 127B S6: the coupon funnel ------------------------------------------------

/** Steps the browser reports (each verified against the ad's ticket). */
export const KUPON_CLIENT_STEPS = ["cpn_tap", "cpn_ok", "cpn_fail", "tray_open", "co_cpn", "co_gap", "co_gap_ok", "co_none", "co_email", "co_pay", "co_leave"] as const;
/** Steps only the server records. */
export const KUPON_SERVER_STEPS = ["order", "paid", "rem_optin", "rem_sent", "rem_click"] as const;
export type KuponStep = (typeof KUPON_CLIENT_STEPS)[number] | (typeof KUPON_SERVER_STEPS)[number];

/** The funnel as the console draws it, top to bottom; imp and click come from iklan_daily. */
export const KUPON_FUNNEL: { key: "imp" | "click" | KuponStep; label: string }[] = [
  { key: "imp", label: "Tayangan kad" },
  { key: "click", label: "Klik" },
  { key: "co_cpn", label: "Kupon terpakai" },
  { key: "co_email", label: "Emel diisi" },
  { key: "co_pay", label: "Tekan Bayar" },
  { key: "order", label: "Pesanan dibuat" },
  { key: "paid", label: "Dibayar" },
];

export interface KuponFunnelRow {
  placement: string;
  imp: number;
  click: number;
  steps: Partial<Record<KuponStep, number>>;
  paidCents: number;
}

export interface KuponFunnelView {
  startDay: string;
  endDay: string;
  total: KuponFunnelRow;
  byPlacement: KuponFunnelRow[];
}

/** Step-to-step rate, as a whole percent; null when the step above is empty. */
export function stepRate(n: number, above: number): number | null {
  return above > 0 ? Math.round((n / above) * 100) : null;
}

/** The ticket's own offer and placement, read without checking it (the server checks). */
export function ticketPayload(ticket: string | null | undefined): { o: string; p: string } | null {
  const body = (ticket ?? "").split(".")[0];
  if (!body) return null;
  try {
    const v = JSON.parse(atob(body.replace(/-/g, "+").replace(/_/g, "/"))) as { o?: unknown; p?: unknown };
    return typeof v.o === "string" && typeof v.p === "string" ? { o: v.o, p: v.p } : null;
  } catch {
    return null;
  }
}
